import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { defaultState, STORAGE_KEY } from './defaultState';
import { SUPPORTED_CURRENCIES } from '../utils/format';

const AppStateContext = createContext(defaultState);
const AppActionsContext = createContext({
  setCurrency: () => {},
  setTheme: () => {},
  updateToolState: () => {},
  replaceToolState: () => {},
  resetAll: () => {},
});

const TOOL_KEYS = Object.keys(defaultState.tools);
const SUPPORTED_THEMES = ['light', 'dark'];

function normalizeTheme(theme, fallback) {
  if (SUPPORTED_THEMES.includes(theme)) {
    return theme;
  }
  if (theme === 'system') {
    return resolveTheme('system');
  }
  return fallback;
}

function resolveTheme(theme, mediaQuery) {
  if (theme === 'system') {
    if (mediaQuery) {
      return mediaQuery.matches ? 'dark' : 'light';
    }
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  }
  return theme === 'light' ? 'light' : 'dark';
}

function applyDocumentTheme(theme, mediaQuery) {
  if (typeof document === 'undefined') {
    return;
  }
  const resolved = resolveTheme(theme, mediaQuery);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

function withDefaults(partial) {
  const base = JSON.parse(JSON.stringify(defaultState));
  if (!partial) {
    return base;
  }
  const merged = { ...base, ...partial };
  merged.tools = { ...base.tools, ...(partial.tools || {}) };
  merged.theme = normalizeTheme(merged.theme, resolveTheme(base.theme));
  if (!SUPPORTED_CURRENCIES.includes(merged.currency)) {
    merged.currency = base.currency;
  }
  for (const tool of TOOL_KEYS) {
    merged.tools[tool] = {
      ...base.tools[tool],
      ...(partial.tools && partial.tools[tool] ? partial.tools[tool] : {}),
    };
  }
  if (!merged.tools.hash && partial.tools && partial.tools.sha256) {
    merged.tools.hash = {
      ...base.tools.hash,
      ...partial.tools.sha256,
    };
    delete merged.tools.sha256;
  }
  if (merged.tools.salary) {
    const salary = merged.tools.salary;
    if (!Array.isArray(salary.sources)) {
      const fallbackAmount = Number(salary.income) || 0;
      salary.sources = [
        {
          id: 'source-primary',
          name: 'Источник 1',
          amount: fallbackAmount,
        },
      ];
    }
    salary.sources = salary.sources.map((source, index) => ({
      id: source.id || `source-${index}`,
      name: source.name ?? `Источник ${index + 1}`,
      amount: Number(source.amount) || 0,
    }));
    delete salary.income;
  }
  return merged;
}

function parseQueryState() {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.size === 0) {
    return null;
  }
  const next = {};
  if (params.has('currency')) {
    const candidate = params.get('currency');
    if (SUPPORTED_CURRENCIES.includes(candidate)) {
      next.currency = candidate;
    }
  }
  if (params.has('theme')) {
    next.theme = normalizeTheme(params.get('theme'), resolveTheme(defaultState.theme));
  }
  next.tools = {};
  let hasTool = false;
  for (const tool of TOOL_KEYS) {
    if (!params.has(tool)) {
      continue;
    }
    try {
      const raw = params.get(tool);
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        next.tools[tool] = parsed;
        hasTool = true;
      }
    } catch (error) {
      // ignore malformed state
    }
  }
  if (!hasTool && !next.currency) {
    return null;
  }
  return withDefaults(next);
}

function loadPersistedState() {
  if (typeof window === 'undefined') {
    return JSON.parse(JSON.stringify(defaultState));
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return JSON.parse(JSON.stringify(defaultState));
    }
    const parsed = JSON.parse(stored);
    return withDefaults(parsed);
  } catch (error) {
    console.warn('Failed to parse stored state', error);
    return JSON.parse(JSON.stringify(defaultState));
  }
}

function getInitialState() {
  const persisted = loadPersistedState();
  const fromQuery = parseQueryState();
  const initial = fromQuery || persisted;
  const resolvedTheme = resolveTheme(initial.theme);
  applyDocumentTheme(resolvedTheme);
  return { ...initial, theme: resolvedTheme };
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_CURRENCY':
      return { ...state, currency: action.payload };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'UPDATE_TOOL_STATE': {
      const { tool, patch } = action.payload;
      if (!TOOL_KEYS.includes(tool)) {
        return state;
      }
      return {
        ...state,
        tools: {
          ...state.tools,
          [tool]: {
            ...state.tools[tool],
            ...(typeof patch === 'function' ? patch(state.tools[tool]) : patch),
          },
        },
      };
    }
    case 'REPLACE_TOOL_STATE': {
      const { tool, value } = action.payload;
      if (!TOOL_KEYS.includes(tool)) {
        return state;
      }
      return {
        ...state,
        tools: {
          ...state.tools,
          [tool]: withDefaults({ tools: { [tool]: value } }).tools[tool],
        },
      };
    }
    case 'RESET':
      return JSON.parse(JSON.stringify(defaultState));
    default:
      return state;
  }
}

export function AppStateProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, defaultState, getInitialState);

  useEffect(() => {
    applyDocumentTheme(state.theme);
  }, [state.theme]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to persist state', error);
    }
  }, [state]);

  const setCurrency = useCallback((code) => {
    if (!SUPPORTED_CURRENCIES.includes(code)) {
      return;
    }
    dispatch({ type: 'SET_CURRENCY', payload: code });
  }, []);

  const setTheme = useCallback((theme) => {
    if (!SUPPORTED_THEMES.includes(theme)) {
      return;
    }
    dispatch({ type: 'SET_THEME', payload: theme });
  }, []);

  const updateToolState = useCallback((tool, patch) => {
    dispatch({ type: 'UPDATE_TOOL_STATE', payload: { tool, patch } });
  }, []);

  const replaceToolState = useCallback((tool, value) => {
    dispatch({ type: 'REPLACE_TOOL_STATE', payload: { tool, value } });
  }, []);

  const resetAll = useCallback(() => {
    dispatch({ type: 'RESET' });
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const value = useMemo(() => state, [state]);
  const actions = useMemo(
    () => ({ setCurrency, setTheme, updateToolState, replaceToolState, resetAll }),
    [setCurrency, setTheme, updateToolState, replaceToolState, resetAll]
  );

  return (
    <AppStateContext.Provider value={value}>
      <AppActionsContext.Provider value={actions}>{children}</AppActionsContext.Provider>
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  return useContext(AppStateContext);
}

export function useAppActions() {
  return useContext(AppActionsContext);
}
